<template>
  <section style="height:100%">
    <tool-bar pageTitle="Flow.Sight">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            v-on="on"
            v-bind="attrs"
            @click="analyticsProStore.toggleDatasetPersistence()"
            :color="analyticsProStore.getDatasetPersistenceEnabled ? 'success' : undefined"
            :outlined="!analyticsProStore.getDatasetPersistenceEnabled"
            small
            label
            class="dataset-persistence-chip"
          >
            <v-icon left small>{{ analyticsProStore.getDatasetPersistenceEnabled ? 'mdi-content-save-check-outline' : 'mdi-content-save-off-outline' }}</v-icon>
            {{ analyticsProStore.getDatasetPersistenceEnabled ? $t('flowSight.datasetPersistence.on') : $t('flowSight.datasetPersistence.off') }}
          </v-chip>
        </template>
        {{ $t('flowSight.datasetPersistence.tooltip') }}
      </v-tooltip> 
    </tool-bar>
    <div v-if="!handleBlur" style="height:100%">
      <upload-csv-modal v-model="showUploadModal" />
      <!-- <analytics-settings v-model="settings" /> -->
      <v-main style="height:100%; background:none" >
        <div class="flowsight-layout">
          <flow-sight-side-menu
            :maxSliderDigits="maxSliderDigits"
            :period-range="periodRange"
            :slider-range="sliderRange"
            :side-switch="sideSwitch"
            :is-period-single-value="isPeriodSingleValue"
            :is-ytd-shortcut-applied="isYtdShortcutApplied"
            :is-mtd-shortcut-applied="isMtdShortcutApplied"
            :is-flow-sight="isFlowSight"
            :nav-items="menuItems"
            :handle-blur="handleBlur"
            @update:sliderRange="sliderRange = $event"
            @update:sideSwitch="sideSwitch = $event"
            @apply-ytd-shortcut="applyYtdShortcut"
            @apply-mtd-shortcut="applyMtdShortcut"
            @check-side-switch="checkSideSwitch"
            @on-period-slider-change="onPeriodSliderChange"
            @open-modal="openModal"
          />
          <div class="flowsight-content">
            <!-- Page navigation moved into the sidebar's `nav` slot, so it
                 survives the sidebar collapsing as an icon rail. -->
            <loading-modal v-model="fetchingData" />
            <keep-alive>
              <router-view />
            </keep-alive>
          </div>
        </div>
      </v-main>
    </div>
    <div v-else>
      <img src="../assets/blur_imgs/blur2.png" :alt="$t('extras.blurredImageAlt')">
    </div>
  </section>
</template>

<script>
import { useAnalyticsProStore } from "@/store/analyticsStore"
import { mapStores } from "pinia"

import ToolBar from "../components/layout/ToolBar.vue";
import UploadCsvModal from "@/components/modal/UploadCsvModal.vue";
import { useCoreStore } from "@/store/coreStore";
// import AnalyticsSettings from "@/components/FlowSight/modal/FlowSightSettings.vue";
import FlowSightSideMenu from "@/components/FlowSight/shared/FlowSightSideMenu.vue";
import { slugOf, productFromPath, flowSightHome } from "@/utils/flowSightProducts";
import LoadingModal from "@/components/modal/LoadingModal.vue";

/**
 * https://bancosafrabeta.octax.pro/apiv1.3/octax/trading/blotter/fxspot/export_transactions?ts_min=2026-04-10&ts_max=2026-04-10
 */
export default {
  components: {
    ToolBar,
    UploadCsvModal,
    // AnalyticsSettings,
    FlowSightSideMenu,
    LoadingModal
  },
  name:"Flow.Sight",
  data() {
    return {
      showUploadModal: false,
      sliderRange: [0, 0],
      settings: false,
      isFlowSight: false,
      sideSwitch: ["BUY", "SELL"],
      lastSwitchPressed: null,
    };
  },
  mounted(){
    // Deep link ou refresh entram direto numa subarvore de produto sem passar
    // pela tela de escolha - a rota e a fonte da verdade nesse caso.
    const daRota = productFromPath(this.$route.path)
    if (daRota) this.analyticsProStore.setFlowSightProduct(daRota)
   //this.analyticsProStore.initAnalytics() //init analytics
    this.analyticsProStore.loadPersistedAnalyticsData()
    this.syncSliderFromStore()
  },
  beforeDestroy() {
    this.analyticsProStore.resetAnalyticsDataIfNotPersisted()
  },
  watch: {
    // O arquivo carregado decide o produto (dataEmitter reconhece o layout do
    // CSV), mas o store nao navega. Sem este watcher, subir um relatorio de
    // spot estando em /flowsight/ndf trocava o produto por baixo e deixava o
    // usuario nos charts de NDF - onde todo prazo de spot cai numa faixa so, e
    // os graficos parecem quebrados. `replace` e nao `push`: e correcao de
    // rota, nao navegacao do usuario, e nao deve entrar no historico.
    'analyticsProStore.flowSightProduct': {
      handler(produto) {
        if (!produto) return
        if (productFromPath(this.$route.path) === produto) return
        this.$router.replace(flowSightHome(produto))
      },
    },

    sideFilter: {
      immediate: true,
      handler (arr) {
        if (arr.length < 1) {
          const restore = this.lastSwitchPressed === 'SELL' ? 'BUY' : 'SELL'
          if (!this.sideSwitch.includes(restore)) this.sideSwitch.push(restore)
          this.analyticsProStore.setSideFilter([restore])
          return
        }
        this.analyticsProStore.setSideFilter(arr)
      }
    },
    'analyticsProStore.periodSelection': {
      deep: true,
      handler(value) {
        this.sliderRange = Array.isArray(value) ? value.slice(0, 2) : [0, 0]
      }
    },
  },
  computed: {
    // Os links seguem o produto da rota atual, senao navegar entre abas jogaria
    // o usuario de volta para a arvore do outro produto.
    menuItems() {
      const base = `/flowsight/${slugOf(this.analyticsProStore.flowSightProduct)}`;
      return [
        {
          title: this.$t('flowSight.tabs.overview'),
          // Real, distinct icons: collapsed, the icon is the only cue. The
          // previous "mdi-chart" is not an MDI name and rendered as nothing.
          icon: "mdi-view-dashboard-outline",
          link: `${base}/overview`,
        },
        {
          title: this.$t('flowSight.tabs.conversion'),
          icon: "mdi-percent-outline",
          link: `${base}/conversion`,
        },
        {
          title: this.$t('flowSight.tabs.portfolio'),
          icon: "mdi-account-group-outline",
          link: `${base}/portfolio`,
        },
      ];
    },

    ...mapStores(useAnalyticsProStore),
    ...mapStores(useCoreStore),
    fetchingData(){
      return this.analyticsProStore.getFetchingData
    },
    handleBlur()
    {
      return this.coreStore.evaluateBlur(this.$options.name)
    },
    periodRange() {
      return this.analyticsProStore.getPeriodRange()
    },
    isPeriodSingleValue() {
      return this.analyticsProStore.isSinglePeriodValue()
    },
    maxSliderDigits() {
      const maxValue = Math.max(Number(this.periodRange.max) || 0, Number(this.periodRange.min) || 0, 0)
      return String(maxValue).length
    },
    ytdShortcutRange() {
      // YTD button is a shortcut to 0-12 months, clamped by available data.
      return [0, Math.min(12, this.periodRange.max)]
    },
    mtdShortcutRange() {
      // MTD button is a shortcut to 0-1 month, clamped by available data.
      return [0, Math.min(1, this.periodRange.max)]
    },
    isYtdShortcutApplied() {
      return this.sliderRange[0] === this.ytdShortcutRange[0] && this.sliderRange[1] === this.ytdShortcutRange[1]
    },
    isMtdShortcutApplied() {
      return this.sliderRange[0] === this.mtdShortcutRange[0] && this.sliderRange[1] === this.mtdShortcutRange[1]
    },
    sideFilter() {
      const arr = []
      if (this.sideSwitch.includes('BUY'))  arr.push('BUY')
      if (this.sideSwitch.includes('SELL'))  arr.push('SELL')
     // if (this.sideSellOn) arr.push('SELL')
      return arr
    },
  },
  methods: {
    openAnalyticsSetting(){
      this.settings = !this.settings
    },
    checkSideSwitch(side){
        this.lastSwitchPressed = side
    },
    syncSliderFromStore() {
      this.sliderRange = Array.isArray(this.analyticsProStore.periodSelection)
        ? this.analyticsProStore.periodSelection.slice(0, 2)
        : [0, 0]
    },
    onPeriodSliderChange(value) {
      const nextRange = Array.isArray(value) ? value.slice(0, 2) : [0, 0]
      this.sliderRange = nextRange
      this.analyticsProStore.setPeriodSelection(nextRange)
    },
    applyYtdShortcut() {
      if (this.isYtdShortcutApplied) {
        this.analyticsProStore.resetPeriodSelection()
        this.syncSliderFromStore()
        return
      }
      this.analyticsProStore.applyYtdShortcut()
      this.syncSliderFromStore()
    },
    applyMtdShortcut() {
      if (this.isMtdShortcutApplied) {
        this.analyticsProStore.resetPeriodSelection()
        this.syncSliderFromStore()
        return
      }
      this.analyticsProStore.applyMtdShortcut()
      this.syncSliderFromStore()
    },
    openModal() {
    if(this.handleBlur === 'blur-background')return;
    this.showUploadModal = true;
      
    },
  },
};
</script>

<style scoped>

::v-deep .v-main__wrap {
  height: 100%;
}

.flowsight-layout {
  display: flex;
  height: 100%;
}

.flowsight-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  display: flex;
  flex-direction: column;
}

.thin-border
{
  border: thin solid white; border-radius: 4px;
}



.v-divider {
  border-color: var(--surface-2) !important;
}
.date-picker-resp{
  width: 100px ;
  height: 250px;
}

.sidebar-actions {
  display: flex;
  flex-direction: column;
}
.sidebar-period {
  display: flex;
  flex-direction: row;
  gap: 0.6rem;
  padding-bottom: 0.5rem;
}

.period-panel {
  display: flex;
  flex-direction: column;
}

.period-slider {
  flex: 1;
  margin: 0 0.35rem;
}

.period-slider-row {
  display: flex;
  align-items: center;
}

.period-bound {
  min-width: 1.5rem;
  text-align: center;
  font-size: 0.8rem;
  color: var(--muted);
}
</style>
